// NationalPartyShareBar.jsx
import { Box } from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from "recharts";

// Colours to match Dutch political parties
const PARTY_COLOURS = {
  D66: "#2cc800",
  PVV: "#043555",
  VVD: "#0e2783",
  'GL-PvdA': "#920c19",
  CDA: "#339933",
  Right: "#7393B3",
  Left: "#997570",
  Centre: "#D3D3D3",
  Other: "#d4d2d2ff",
  Local: "#666666",
};

// SVG path for per-corner rounded rect
function roundedRectPath(x, y, w, h, rTL, rTR, rBR, rBL) {
  // clamp radii
  const maxR = Math.min(w, h) / 2;
  const tl = Math.max(0, Math.min(rTL, maxR));
  const tr = Math.max(0, Math.min(rTR, maxR));
  const br = Math.max(0, Math.min(rBR, maxR));
  const bl = Math.max(0, Math.min(rBL, maxR));

  return `
    M ${x + tl},${y}
    H ${x + w - tr}
    Q ${x + w},${y} ${x + w},${y + tr}
    V ${y + h - br}
    Q ${x + w},${y + h} ${x + w - br},${y + h}
    H ${x + bl}
    Q ${x},${y + h} ${x},${y + h - bl}
    V ${y + tl}
    Q ${x},${y} ${x + tl},${y}
    Z
  `;
}

// Custom label logic (same behaviour as your SegmentLabel)
function renderSegmentLabel({ x, y, width, height, value, name }) {
  if (width < 20) return null;

  const cx = x + width / 2;
  const cy = y + height / 2;

  const pctText = `${Math.round(value)}%`;
  const onlyPct = width < 10;

  return (
    <g key={`lbl-${name}`} style={{ pointerEvents: "none" }}>
      {!onlyPct && (
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontWeight="700"
          fontSize="10"
        >
          {name}
        </text>
      )}
      <text
        x={cx}
        y={onlyPct ? cy : cy + 8}
        textAnchor="middle"
        dominantBaseline="central"
        fill="#fff"
        fontWeight="600"
        fontSize="10"
      >
        {pctText}
      </text>
    </g>
  );
}

// matcher because named the columns stupidly
function getGroupKeys(polLevel) {
  const forward =
    polLevel === "past"
      ? {
          D66: "past_vote_D66",
          CDA: "past_vote_CDA",
          'GL-PvdA': "past_vote_GLpvdA",
          Centre: "past_vote_OtherCentre",
          Left: "past_vote_OtherLeft",
          Right: "past_vote_OtherRight",
          PVV: "past_vote_PVV",
          VVD: "past_vote_VVD",
        }
      : (() => {
          const level = polLevel === "LE" ? "LE" : "GE";

          return {
            D66: `D66_${level}_p`,
            PVV: `PVV_${level}_p`,
            VVD: `VVD_${level}_p`,
            'GL-PvdA': `GLpvdA_${level}_p`,
            CDA: `CDA_${level}_p`,
            ...(polLevel === "LE" ? { Local: "Local_LE_p" } : {}),
            Right: `OtherRight_${level}_p`,
            Left: `OtherLeft_${level}_p`,
            Centre: `OtherCentre_${level}_p`,
            Other: `Other_${level}_p`,
          };
        })();

  // invert it: longKey -> shortName
  return Object.fromEntries(
    Object.entries(forward).map(([shortName, longKey]) => [longKey, shortName])
  );
}

// ✅ Single-bar custom shape that draws the full segmented bar in the SERIES order
function SegmentedBarShape({ x, y, width, height, payload, series }) {
  const r = 6; // keep your original rounding feel

  // Scale to the chart width based on 0–100 axis
  const scale = width / 100;

  let cursorX = x;

  return (
    <g>
      {series.map((key) => {
        const v = Number(payload?.[key]) || 0;
        if (v <= 0) return null;

        const segW = v * scale;

        const isLeftEnd = key === "GLpvdA";
        const isRightEnd = key === "PVV";

        const path = roundedRectPath(
          cursorX,
          y,
          segW,
          height,
          isLeftEnd ? r : 0,   // TL
          isRightEnd ? r : 0,  // TR
          isRightEnd ? r : 0,  // BR
          isLeftEnd ? r : 0    // BL
        );

        const fill = PARTY_COLOURS?.[key] || "#ccc";

        const label = renderSegmentLabel({
          x: cursorX,
          y,
          width: segW,
          height,
          value: v,
          name: key,
        });

        const out = (
          <g key={`seg-${key}`}>
            <path d={path} fill={fill} stroke="#ffffff" strokeWidth={2} />
            {label}
          </g>
        );

        cursorX += segW;
        return out;
      })}
    </g>
  );
}

export default function NationalPartyShareBar({
  metricRowsConst,
  metricRowsNat,
  selectedFeature,
  WIDTH,
  polLevel,
}) {
  if (!metricRowsNat && !metricRowsConst) return null;

  const safeArray = (v) => (Array.isArray(v) ? v : []);
  const constRows = safeArray(metricRowsConst);
  const natRows = safeArray(metricRowsNat);

  let row = {};
  const hasSelectedAreaId =
    selectedFeature != null && selectedFeature.area_id != null;

  if (hasSelectedAreaId && constRows.length > 0) {
    const selectedId = selectedFeature.area_id;
    row = constRows.find((g) => g?.area_id === selectedId) ?? {};
  } else {
    row = natRows[0] ?? {};
  }

  const toPct = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? num : 0;
  };

  const longToShort = getGroupKeys(polLevel);

  const dataPoint = { id: "NationalRow" };
  for (const [longKey, shortName] of Object.entries(longToShort)) {
    dataPoint[shortName] = toPct(row?.[longKey]);
  }

  // ✅ Local forced into the middle (exactly where you want it)
  const SERIES =
    polLevel === "LE"
      ? [
          "GL-PvdA",
          "D66",
          "Left",
          "Centre",
          "Local", // middle
          "Other",
          "Right",
          "CDA",
          "VVD",
          "PVV",
        ]
      : ["GL-PvdA", "D66", "Left", "Centre","Other", "Right", "CDA", "VVD", "PVV"];

  // One row, one bar. X axis is 0–100 so the custom shape can map % to pixels.
  const data = [{ ...dataPoint, total: 100 }];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignContent: "center",
        width: WIDTH,
        px: 0,
      }}
    >
      <ResponsiveContainer width="100%" height={48}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          barCategoryGap={0}
        >
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="id" hide />

          <Bar
            dataKey="total"
            isAnimationActive={false}
            // ✅ draw the whole segmented bar ourselves
            shape={(props) => (
              <SegmentedBarShape {...props} series={SERIES} />
            )}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
